import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import loginimage from "../assets/loginn.png";

import "../Styles/Login.css";

import {
  FaShieldAlt,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaTimes,
  FaSyncAlt,
  FaCheckCircle,
  FaEnvelope,
} from "react-icons/fa";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ApiService from "../nhrc_apis/ApiService";

const { AuthAPI, PasswordAPI } = ApiService;

function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [rememberMe, setRememberMe] = useState(false);

  const [captchaQuestion, setCaptchaQuestion] = useState("");
  const [captchaId, setCaptchaId] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");

  const [captchaVerified, setCaptchaVerified] = useState(false);

  const [loadingCaptcha, setLoadingCaptcha] = useState(false);

  const [loading, setLoading] = useState(false);

  /* =========================================================
     ✅ CORRECTED FORGOT PASSWORD STATES

     Real flow (confirmed via Swagger):
       Step 1: enter email → PasswordAPI.forgotPassword(email)
               (backend emails an OTP)
       Step 2: enter OTP + new password + confirm password →
               PasswordAPI.resetPassword({ email, otp,
               new_password, confirm_password }) directly.
               There is NO separate "verify OTP" step and NO
               reset token — the OTP is submitted straight to
               reset-password along with the new password.
  ========================================================= */

  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);

  const [forgotEmail, setForgotEmail] = useState("");
  const [sendingForgotOtp, setSendingForgotOtp] = useState(false);

  const [forgotOtp, setForgotOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  useEffect(() => {
    loadCaptcha();
  }, []);

  const loadCaptcha = async () => {
    try {
      setLoadingCaptcha(true);
      setCaptchaVerified(false);
      setCaptchaAnswer("");

      const res = await AuthAPI.getCaptcha();

      setCaptchaQuestion(res?.data?.question || "");
      setCaptchaId(res?.data?.captcha_id || "");
    } catch (err) {
      console.error("CAPTCHA ERROR:", err);

      toast.error("Failed to load captcha");

      setCaptchaQuestion("");
      setCaptchaId("");
    } finally {
      setLoadingCaptcha(false);
    }
  };

  const handleVerifyCaptcha = async () => {
    if (!captchaAnswer.trim()) {
      toast.error("Enter captcha answer");
      return;
    }

    if (!captchaId) {
      toast.error("Captcha is not available. Please refresh.");
      return;
    }

    try {
      await AuthAPI.verifyCaptcha({
        captcha_id: captchaId,
        answer: captchaAnswer.trim(),
      });

      setCaptchaVerified(true);

      toast.success("Captcha verified successfully");
    } catch (err) {
      console.error("CAPTCHA VERIFY ERROR:", err);

      setCaptchaVerified(false);

      toast.error(
        err?.response?.data?.detail ||
          "Captcha verification failed"
      );

      loadCaptcha();
    }
  };

  const handleClose = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  /* =========================================================
     ✅ FORGOT PASSWORD — OPEN/CLOSE MODAL
  ========================================================= */

  const openForgotModal = () => {
    setShowForgotModal(true);
    setForgotStep(1);
    setForgotEmail(email.trim());
    setForgotOtp("");
    setNewPassword("");
    setConfirmNewPassword("");
  };

  const closeForgotModal = () => {
    setShowForgotModal(false);
  };

  /* =========================================================
     ✅ FORGOT PASSWORD — STEP 1: SEND OTP
     POST /password/forgot-password?email=...
  ========================================================= */

  const handleSendForgotOtp = async () => {
    if (!forgotEmail.trim()) {
      toast.error("Please enter your email address.");
      return;
    }

    try {
      setSendingForgotOtp(true);

      await PasswordAPI.forgotPassword(
        forgotEmail.trim().toLowerCase()
      );

      toast.success("OTP sent to your email");

      setForgotStep(2);
    } catch (err) {
      console.error("FORGOT PASSWORD ERROR:", err);

      toast.error(
        err?.response?.data?.detail ||
          "Failed to send OTP. Please check the email and try again."
      );
    } finally {
      setSendingForgotOtp(false);
    }
  };

  /* =========================================================
     ✅ FORGOT PASSWORD — STEP 2: RESET WITH OTP
     POST /password/reset-password
       { email, otp, new_password, confirm_password }
  ========================================================= */

  const handleResetPassword = async () => {
    if (!forgotOtp.trim()) {
      toast.error("Please enter the OTP.");
      return;
    }

    if (!newPassword.trim() || !confirmNewPassword.trim()) {
      toast.error("Please fill both password fields.");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    try {
      setResettingPassword(true);

      await PasswordAPI.resetPassword({
        email: forgotEmail.trim().toLowerCase(),
        otp: forgotOtp.trim(),
        new_password: newPassword.trim(),
        confirm_password: confirmNewPassword.trim(),
      });

      toast.success("Password reset successfully! Please log in.");

      closeForgotModal();
    } catch (err) {
      console.error("RESET PASSWORD ERROR:", err);

      toast.error(
        err?.response?.data?.detail || "Failed to reset password"
      );
    } finally {
      setResettingPassword(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error("Please enter your email address.");
      return;
    }

    if (!password.trim()) {
      toast.error("Please enter your password.");
      return;
    }

    if (!captchaVerified) {
      toast.error("Please verify captcha first");
      return;
    }

    if (!captchaId || !captchaAnswer.trim()) {
      toast.error("Captcha session expired. Please verify again.");
      setCaptchaVerified(false);
      loadCaptcha();
      return;
    }

    try {
      setLoading(true);

      localStorage.removeItem("adminToken");
      localStorage.removeItem("employeeToken");
      localStorage.removeItem("studentToken");
      localStorage.removeItem("token");
      localStorage.removeItem("user");

      const response = await ApiService.AuthAPI.adminLogin({
        email: email.trim().toLowerCase(),
        password: password.trim(),
        captcha_id: captchaId,
        captcha: captchaAnswer.trim(),
      });

      console.log("LOGIN RESPONSE:", response);

      const token =
        response?.data?.access_token ||
        response?.data?.token ||
        response?.data?.jwt ||
        "";

      const user = response?.data?.user || response?.data?.admin || {};

      if (!token) {
        toast.error("Login failed: No authentication token received.");
        return;
      }

      const safeUser = {
        id: user?.id || user?.admin_id || "",
        name:
          user?.name ||
          user?.full_name ||
          user?.fullName ||
          user?.username ||
          "Admin",
        email: user?.email || email.trim().toLowerCase(),
        role: user?.role || "ADMIN",
      };

      localStorage.setItem("adminToken", token);
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(safeUser));
      localStorage.setItem("rememberMe", rememberMe ? "true" : "false");

      toast.success("Admin Login Successful!");

      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 1000);

    } catch (error) {
      console.log("LOGIN ERROR:", error);
      console.log("ERROR RESPONSE:", error?.response);

      if (error?.response) {
        if (error.response.status === 401) {
          toast.error(
            error?.response?.data?.detail || "Invalid email or password"
          );
        } else if (error.response.status === 422) {
          const detail = error?.response?.data?.detail;
          const detailMsg = Array.isArray(detail)
            ? (detail[0]?.msg || "Validation Error")
            : (detail || "Validation Error");
          toast.error(detailMsg);
        } else if (error.response.status === 500) {
          toast.error("Internal Server Error. Please try again later.");
        } else {
          toast.error(error?.response?.data?.detail || "Login failed");
        }
      } else {
        toast.error("Network error. Please check your connection.");
      }

      loadCaptcha();
      setCaptchaVerified(false);

    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
      />

      <div className="unique-login-wrapper">

        <div className="unique-login-card">

          <div className="row g-0 h-100">

            <div className="col-md-5 d-none d-md-block unique-login-left">
              <img
                src={loginimage}
                className="unique-login-image"
                alt="NHRC Admin Login"
              />
            </div>

            <div className="col-md-7 col-12 unique-login-right">

              <button
                type="button"
                className="login-close-btn"
                onClick={handleClose}
                aria-label="Close login"
              >
                <FaTimes />
              </button>

              <div className="unique-login-body">

                <div className="admin-login-icon">
                  <FaShieldAlt />
                </div>

                <h3 className="unique-login-title">
                  Admin Login
                </h3>

                <p className="admin-welcome">
                  Welcome back! Please sign in to continue.
                </p>

                <div className="title-line"></div>

                <form onSubmit={handleLogin}>

                  <div className="login-field">
                    <label className="small-label">Email Address</label>
                    <div className="input-wrapper">
                      <span className="input-icon">✉</span>
                      <input
                        type="email"
                        className="form-control unique-login-input"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoComplete="email"
                        required
                      />
                    </div>
                  </div>

                  <div className="login-field">
                    <label className="small-label">Password</label>
                    <div className="input-wrapper">
                      <span className="input-icon"><FaLock /></span>
                      <input
                        type={showPassword ? "text" : "password"}
                        className="form-control unique-login-input"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        className="password-eye"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  <div className="captcha-row">
                    <div className="captcha-question">
                      {loadingCaptcha ? "Loading..." : captchaQuestion || "Captcha"}
                    </div>
                    <input
                      type="text"
                      className="captcha-input"
                      placeholder="Enter Answer"
                      value={captchaAnswer}
                      onChange={(e) => {
                        setCaptchaAnswer(e.target.value);
                        setCaptchaVerified(false);
                      }}
                      disabled={loadingCaptcha}
                    />
                    <button
                      type="button"
                      className="captcha-refresh"
                      onClick={loadCaptcha}
                      aria-label="Refresh captcha"
                      disabled={loadingCaptcha}
                    >
                      <FaSyncAlt />
                    </button>
                  </div>

                  <button
                    type="button"
                    className={
                      captchaVerified
                        ? "captcha-verify-btn verified"
                        : "captcha-verify-btn"
                    }
                    onClick={handleVerifyCaptcha}
                    disabled={loadingCaptcha || captchaVerified}
                  >
                    {captchaVerified ? (
                      <>
                        <FaCheckCircle />
                        Verified
                      </>
                    ) : (
                      "Verify Captcha"
                    )}
                  </button>

                  {captchaVerified && (
                    <div className="captcha-success">
                      <FaCheckCircle />
                      <span>Captcha verified successfully!</span>
                    </div>
                  )}

                  <div className="login-options">
                    <label className="remember-me">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <span>Remember Me</span>
                    </label>

                    <button
                      type="button"
                      className="unique-forget"
                      onClick={openForgotModal}
                    >
                      Forgot Password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    className="btn unique-btn1"
                    disabled={loading}
                  >
                    <FaLock className="login-lock-icon" />
                    <span>{loading ? "Logging in..." : "Login"}</span>
                  </button>

                </form>

                <p className="login-copyright">
                  © 2026 National Human Resource Club.
                  All Rights Reserved.
                </p>

              </div>

            </div>

          </div>

        </div>

      </div>

      {/* =========================================================
          ✅ CORRECTED FORGOT PASSWORD MODAL — 2 steps now, not 3.
          Step 2 submits OTP + new password directly to
          reset-password; there is no separate verify-OTP call.
      ========================================================= */}

      {showForgotModal && (
        <div
          className="forgot-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="forgot-modal-card"
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "28px",
              width: "100%",
              maxWidth: "400px",
              position: "relative",
            }}
          >
            <button
              type="button"
              className="forgot-modal-close"
              onClick={closeForgotModal}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "none",
                border: "none",
                fontSize: "18px",
                cursor: "pointer",
              }}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <h4 style={{ marginBottom: "8px" }}>Reset Password</h4>

            {/* ============ STEP 1: EMAIL ============ */}
            {forgotStep === 1 && (
              <>
                <p style={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
                  Enter your email address and we'll send you an OTP.
                </p>

                <div className="input-wrapper" style={{ marginBottom: "16px" }}>
                  <span className="input-icon"><FaEnvelope /></span>
                  <input
                    type="email"
                    className="form-control unique-login-input"
                    placeholder="Enter your email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                  />
                </div>

                <button
                  type="button"
                  className="btn unique-btn1"
                  style={{ width: "100%" }}
                  disabled={sendingForgotOtp}
                  onClick={handleSendForgotOtp}
                >
                  {sendingForgotOtp ? "Sending OTP..." : "Send OTP"}
                </button>
              </>
            )}

            {/* ============ STEP 2: OTP + NEW PASSWORD ============ */}
            {forgotStep === 2 && (
              <>
                <p style={{ fontSize: "14px", color: "#666", marginBottom: "16px" }}>
                  Enter the OTP sent to <b>{forgotEmail}</b> and your new password.
                </p>

                <input
                  type="text"
                  className="form-control unique-login-input"
                  placeholder="Enter OTP"
                  value={forgotOtp}
                  onChange={(e) => setForgotOtp(e.target.value)}
                  style={{ marginBottom: "12px" }}
                />

                <div className="input-wrapper" style={{ marginBottom: "12px" }}>
                  <span className="input-icon"><FaLock /></span>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    className="form-control unique-login-input"
                    placeholder="New password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="password-eye"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <div className="input-wrapper" style={{ marginBottom: "16px" }}>
                  <span className="input-icon"><FaLock /></span>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    className="form-control unique-login-input"
                    placeholder="Confirm new password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                  />
                </div>

                <button
                  type="button"
                  className="btn unique-btn1"
                  style={{ width: "100%", marginBottom: "8px" }}
                  disabled={resettingPassword}
                  onClick={handleResetPassword}
                >
                  {resettingPassword ? "Resetting..." : "Reset Password"}
                </button>

                <button
                  type="button"
                  className="captcha-refresh"
                  style={{ width: "100%" }}
                  disabled={sendingForgotOtp}
                  onClick={handleSendForgotOtp}
                >
                  Resend OTP
                </button>
              </>
            )}

          </div>
        </div>
      )}
    </>
  );
}

export default Login;